/**
 * Statusi gost-narudžbi: redosled u meniju, boje i šta se računa kao "aktivna" narudžba.
 * Isti izvor koristi stari izveštaj (hasGuestOrderStatus) i novi izgled (guest-report).
 */
// zamrznuto: koristi se u data() više komponenti, Vue ga ne treba da posmatra
export const ORDER_STATUSES = Object.freeze(
  [
    // "pending" nije u meniju: backend poznaje samo "on-hold" (narudžba čeka potvrdu restorana), vidi menuState
    { value: "on-hold", icon: "mdi-clock-outline" },
    { value: "accepted", icon: "mdi-check" },
    { value: "in-progress", icon: "mdi-progress-clock" },
    { value: "ready", icon: "mdi-food" },
    { value: "delivered", icon: "mdi-truck-check" },
    { value: "completed", icon: "mdi-check-all" },
    // "cancelled": otkazao kupac; "rejected": odbio restoran
    { value: "cancelled", icon: "mdi-cancel" },
    { value: "rejected", icon: "mdi-close-circle-outline" },
  ].map((status) => Object.freeze(status))
);

export const STATUS_COLORS = {
  pending: { fg: "#92620a", bg: "#fbeed2" },
  accepted: { fg: "#1d4ed8", bg: "#e3ebfc" },
  "in-progress": { fg: "#0e7c86", bg: "#ddf3f4" },
  ready: { fg: "#6d28d9", bg: "#eee4fb" },
  "on-hold": { fg: "#92620a", bg: "#fbeed2" },
  delivered: { fg: "#157f3c", bg: "#e0f5e6" },
  completed: { fg: "#157f3c", bg: "#e0f5e6" },
  cancelled: { fg: "#b42318", bg: "#fae3e1" },
  "support-canceled": { fg: "#b42318", bg: "#fae3e1" },
  rejected: { fg: "#b42318", bg: "#fae3e1" },
  deleted: { fg: "#b42318", bg: "#fae3e1" },
};

const FALLBACK_COLORS = { fg: "#55606b", bg: "#eaecee" };

export const getStatusColors = (status) => STATUS_COLORS[status] || FALLBACK_COLORS;

/** Vrste isporuke narudžbe (delivery_type) */
export const DELIVERY_TYPES = Object.freeze(
  [
    { id: 0, key: "home_delivery", icon: "mdi-moped-outline" },
    { id: 1, key: "pickup_at_restaurant", icon: "mdi-storefront-outline" },
    { id: 2, key: "dine_in_restaurant", icon: "mdi-silverware-fork-knife" },
    { id: 5, key: "express_mail", icon: "mdi-email-fast-outline" },
  ].map((type) => Object.freeze(type))
);

/**
 * Stanja koja server vraća, a ekrani ih vode pod imenom koje imaju u svojim menijima: otkazano ("support-canceled",
 * "customer-canceled"), "u pripremi" ("preparing") i "završeno" (backend narudžbu koju smo označili "completed" čuva
 * kao "payed"). Isto važi za listu narudžbi i za kuhinjski ekran.
 */
const BOARD_STATES = {
  "support-canceled": "cancelled",
  "customer-canceled": "cancelled",
  preparing: "in-progress",
  payed: "completed",
};
export const boardState = (state) => BOARD_STATES[state] || state;

/** Isto, uz "pending" (staro ime za "on-hold", isti prevod) koje meniji i filteri spajaju sa "na čekanju". */
export const menuState = (state) => boardState(state === "pending" ? "on-hold" : state);

/**
 * Šta se šalje glavnom API-ju (`/reports/change-order-status`) za vrednost iz menija. Server prima samo imena iz svog
 * enuma stanja ("Nepoznat status narudžbe" za ostalo; proba 2.11: on-hold, accepted, ready, delivered i rejected rade,
 * in-progress, completed i cancelled ne). Obrnut smer je u `menuState`.
 */
const SERVER_STATES = {
  "in-progress": "preparing",
  completed: "payed",
  cancelled: "customer-canceled",
  // "vrati u Naručeno" (kuhinjski ekran, detalji narudžbe): server ulazno stanje zna kao "on-hold"
  pending: "on-hold",
};
export const serverState = (state) => SERVER_STATES[state] || state;

/**
 * PRIVREMENO (backend nalaz 02.11.2026, vidi dostavljaci-front/docs/2026/11/02_11_2026_E2E_test_narudzbe.md): kad je
 * narudžba kod dostavljača (rezervisao, zadužio, u restoranu, stigao), svaka promena statusa od restorana kroz
 * `/reports/change-order-status` prepiše stanje isporuke i skine dostavljača sa narudžbe (dostavljač je gubi, a dispečer je
 * opet vidi kao "čeka kurira"). Dok to backend ne popravi:
 *  - "spremno" se šalje kao vreme spremnosti (`POST /orders/{id}/ready`, bez promene stanja),
 *  - promene koje bi skinule dostavljača se ne šalju (poruka `courier_lock_blocked`),
 *  - otkazivanje i odbijanje ostaju moguća.
 * Kad backend popravi, dovoljno je `COURIER_LOCK_GUARD = false`.
 */
export const COURIER_LOCK_GUARD = true;

// order_guest_status.status: 3 rezervisao, 5 zadužio, 11 kurir u restoranu, 12 kurir stigao (izveštaj ih vraća i kao tekst u `state`)
const COURIER_HELD_CODES = [3, 5, 11, 12];
const COURIER_HELD_STATES = ["booked-delivery", "charged-delivery", "courier-at-restaurant", "courier-arrived"];
const HELD_ALLOWED_TARGETS = ["customer-canceled", "rejected", "support-canceled", "deleted"];

/** Da li je narudžba u rukama dostavljača. Mora da se pozove na objektu pre nego što se `state` prepiše novim statusom. */
export const isHeldByCourier = (order) => {
  const status = order && order.order_guest_status ? order.order_guest_status.status : undefined;
  return COURIER_HELD_CODES.includes(Number(status)) || COURIER_HELD_STATES.includes(status);
};

/** Koje promene statusa i dalje idu kad je narudžba kod dostavljača (otkazivanje, odbijanje): posle njih ona više nije kod njega. */
export const releasesCourier = (serverTarget) => HELD_ALLOWED_TARGETS.includes(serverTarget);

/** "send" (normalno), "ready-time" (samo vreme spremnosti) ili "block" (ne šalje se), za status kakav server prima. */
export const courierLockAction = (held, serverTarget) => {
  if (!COURIER_LOCK_GUARD || !held) return "send";
  if (serverTarget === "ready") return "ready-time";
  return releasesCourier(serverTarget) ? "send" : "block";
};

/** Završeno ili otkazano: narudžba više ne čeka restoran. */
const CLOSED_STATES = ["delivered", "completed", "cancelled", "support-canceled", "rejected", "deleted"];

export const isActiveState = (state) => !CLOSED_STATES.includes(menuState(state));

